/**
 * Submissions UI Store
 *
 * Zustand store for ephemeral (non-persisted) submission inbox state.
 * Manages search, filters, sort, selection, and expanded detail.
 *
 * The actual submission data comes from React Query hooks in
 * src/lib/supabase/submissions.ts. This store only manages UI state.
 */

import { useMemo } from 'react';
import { create } from 'zustand';
import type { Submission, SubmissionStatus } from '@/lib/supabase/submissions';

// ============================================================================
// Types
// ============================================================================

export type SubmissionSortBy =
  | 'newest'
  | 'oldest'
  | 'score-high'
  | 'score-low'
  | 'company-az'
  | 'company-za';

export interface SubmissionFilters {
  query: string;
  statuses: SubmissionStatus[];
  minScore: number | null;
  maxScore: number | null;
  dateRange: 'all' | '7d' | '30d' | '90d';
}

export const DEFAULT_SUBMISSION_FILTERS: SubmissionFilters = {
  query: '',
  statuses: [],
  minScore: null,
  maxScore: null,
  dateRange: 'all',
};

/**
 * Every member of the `SubmissionStatus` union, as a runtime list.
 *
 * Built from a `Record<SubmissionStatus, true>` on purpose: adding a status to
 * the union without adding it here is a COMPILE error, which is what makes the
 * chip-exhaustiveness test in `__tests__/submission-status-filters.test.ts`
 * trustworthy rather than a snapshot that rots.
 */
const SUBMISSION_STATUS_PRESENCE: Record<SubmissionStatus, true> = {
  pending: true,
  scoring: true,
  scored: true,
  shortlisted: true,
  archived: true,
  rejected: true,
  unscorable: true,
};

export const ALL_SUBMISSION_STATUSES = Object.keys(
  SUBMISSION_STATUS_PRESENCE,
) as SubmissionStatus[];

/**
 * Status chips for the submissions inbox.
 *
 * A chip maps to a LIST of statuses, not one, because the pipeline's real states
 * do not line up 1:1 with useful filters:
 *
 *   - `pending` is legacy. `process-submission` writes 'scoring' on both insert
 *     and replace, then 'scored' | 'unscorable'. Nothing has written 'pending'
 *     since the extract→promote→score cutover, so a standalone "Pending" chip
 *     matched nothing — while rows that predate the cutover still carry it.
 *     Folding both into "In progress" keeps those rows findable and removes the
 *     dead chip.
 *
 *   - `unscorable` had no chip at all, so a deck that genuinely could not be
 *     scored was unfilterable.
 *
 * SEVEN CHIPS BECAME FIVE (2026-08-12), and the rule that shrank them is which
 * statuses are TERMINAL. `filterSubmissionsByStatus` hides only terminal ones
 * from the default view, so only those NEED a chip — it is the sole way back to
 * them. Everything else is already on screen, and a chip merely isolates it.
 *
 * Dropped on that basis: `Scored` (nearly every row in the default view is
 * scored, so it isolated almost nothing) and `In progress` (scoring/pending is
 * transient — a permanent button for a state that lasts seconds). Both remain
 * fully visible under `New`.
 *
 * `Unscorable` was KEPT despite being non-terminal: it is the one non-terminal
 * state that represents a problem rather than progress, and a deck that could
 * not be scored is the thing an investor most needs to find.
 *
 * The words now match the pipeline where the concepts match. The inbox and
 * dealflow are one journey, and two vocabularies for it made the hand-off read
 * as a different system: `Rejected` is the dealflow stage of the same name, and
 * the default view is `New` — how an untouched deal reads on the pipeline —
 * rather than the inbox-only word `Active`.
 *
 * `all` carries an EMPTY status list deliberately: an empty selection is the
 * signal `filterSubmissionsByStatus` uses to apply the active-inbox rule (hide
 * terminal statuses). Expanding it to every status would show the archive.
 *
 * `invited` / `claimed` are absent because they are informational TAGS derived
 * from `submission_invites` and `submissions.claimed_by`, not statuses.
 */
export interface SubmissionStatusFilter {
  key: string;
  label: string;
  statuses: SubmissionStatus[];
}

export const SUBMISSION_STATUS_FILTERS: readonly SubmissionStatusFilter[] = [
  { key: 'all', label: 'New', statuses: [] },
  { key: 'shortlisted', label: 'Shortlisted', statuses: ['shortlisted'] },
  { key: 'unscorable', label: 'Unscorable', statuses: ['unscorable'] },
  { key: 'rejected', label: 'Rejected', statuses: ['rejected'] },
  { key: 'archived', label: 'Archived', statuses: ['archived'] },
];

/** Resolve a chip key to its statuses. Unknown keys yield [] (the Active view). */
export function statusesForFilterKey(key: string): SubmissionStatus[] {
  return SUBMISSION_STATUS_FILTERS.find((f) => f.key === key)?.statuses ?? [];
}

/**
 * Terminal submission statuses that leave the ACTIVE inbox.
 *
 * These are the investor DECISIONS that move a submission out of the working
 * queue: `shortlisted` → the investor's dealflow; `rejected`/`archived` → the
 * archive. They remain reachable by selecting the matching status chip.
 *
 * NOT terminal (they are informational TAGS, not removals, and stay in the
 * active inbox with a badge): `invited` (investor invited the founder to claim)
 * and `claimed` (the founder has officially claimed their Inverio profile after
 * submitting). A claimed/invited deck is still an active submission the investor
 * can shortlist or reject.
 */
export const TERMINAL_SUBMISSION_STATUSES: readonly SubmissionStatus[] = [
  'shortlisted',
  'archived',
  'rejected',
];

/**
 * Apply the inbox status filter.
 * - Explicit selection → exact match (how the Shortlisted/Rejected archives
 *   are viewed).
 * - No selection → the ACTIVE inbox: terminal statuses are excluded so a
 *   shortlisted/rejected deck leaves the working queue.
 *
 * Pure + exported so the behavior is unit-testable without rendering the hook.
 */
export function filterSubmissionsByStatus(
  submissions: Submission[],
  statuses: SubmissionStatus[],
): Submission[] {
  if (statuses.length > 0) {
    return submissions.filter((s) => statuses.includes(s.status));
  }
  return submissions.filter((s) => !TERMINAL_SUBMISSION_STATUSES.includes(s.status));
}

// ============================================================================
// Store Interface
// ============================================================================

interface SubmissionsUIState {
  // Filters
  filters: SubmissionFilters;
  updateFilter: <K extends keyof SubmissionFilters>(key: K, value: SubmissionFilters[K]) => void;
  toggleStatusFilter: (status: SubmissionStatus) => void;
  /** Select/clear a whole chip group at once (see SUBMISSION_STATUS_FILTERS). */
  setStatusGroup: (statuses: SubmissionStatus[]) => void;
  resetFilters: () => void;
  activeFilterCount: number;

  // Sort
  sortBy: SubmissionSortBy;
  setSortBy: (sort: SubmissionSortBy) => void;

  // Selection (for bulk actions)
  selectedIds: Set<string>;
  isSelectionMode: boolean;
  toggleSelection: (id: string) => void;
  selectAll: (ids: string[]) => void;
  clearSelection: () => void;
  enterSelectionMode: () => void;
  exitSelectionMode: () => void;

  // Expanded detail
  expandedId: string | null;
  setExpandedId: (id: string | null) => void;
}

// ============================================================================
// Helper — compute active filter count
// ============================================================================

function computeActiveFilterCount(filters: SubmissionFilters): number {
  let count = 0;
  if (filters.statuses.length > 0) count++;
  if (filters.minScore !== null) count++;
  if (filters.maxScore !== null) count++;
  if (filters.dateRange !== 'all') count++;
  return count;
}

// ============================================================================
// Store — no persistence (ephemeral only)
// ============================================================================

export const useSubmissionsUIStore = create<SubmissionsUIState>()((set, get) => ({
  // Filters
  filters: { ...DEFAULT_SUBMISSION_FILTERS },
  activeFilterCount: 0,

  updateFilter: (key, value) => {
    const newFilters = { ...get().filters, [key]: value };
    set({
      filters: newFilters,
      activeFilterCount: computeActiveFilterCount(newFilters),
    });
  },

  setStatusGroup: (statuses) => {
    // Whole-group toggle: a chip that maps to several statuses ("In progress" =
    // scoring + legacy pending) must select or clear all of them together, or
    // the chip renders half-active and filters unpredictably.
    const current = get().filters.statuses;
    const alreadyActive =
      statuses.length > 0 && statuses.every((s) => current.includes(s));
    const newStatuses = alreadyActive
      ? current.filter((s) => !statuses.includes(s))
      : Array.from(new Set([...current, ...statuses]));
    const newFilters = { ...get().filters, statuses: newStatuses };
    set({
      filters: newFilters,
      activeFilterCount: computeActiveFilterCount(newFilters),
    });
  },

  toggleStatusFilter: (status) => {
    const current = get().filters.statuses;
    const newStatuses = current.includes(status)
      ? current.filter((s) => s !== status)
      : [...current, status];
    const newFilters = { ...get().filters, statuses: newStatuses };
    set({
      filters: newFilters,
      activeFilterCount: computeActiveFilterCount(newFilters),
    });
  },

  resetFilters: () =>
    set({
      filters: { ...DEFAULT_SUBMISSION_FILTERS },
      activeFilterCount: 0,
    }),

  // Sort
  sortBy: 'newest',
  setSortBy: (sort) => set({ sortBy: sort }),

  // Selection
  selectedIds: new Set<string>(),
  isSelectionMode: false,

  toggleSelection: (id) => {
    const next = new Set(get().selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    set({ selectedIds: next });
  },

  selectAll: (ids) => set({ selectedIds: new Set(ids) }),

  clearSelection: () => set({ selectedIds: new Set<string>() }),

  enterSelectionMode: () => set({ isSelectionMode: true }),

  exitSelectionMode: () =>
    set({
      isSelectionMode: false,
      selectedIds: new Set<string>(),
    }),

  // Expanded detail
  expandedId: null,
  setExpandedId: (id) => set({ expandedId: id }),
}));

// ============================================================================
// Derived Hook — filter + sort submissions
// ============================================================================

/**
 * Apply the current store filters and sort to a raw submissions array.
 *
 * This hook reads from the Zustand store via selectors and returns a
 * filtered + sorted copy of the input data.
 */
export function useFilteredSubmissions(submissions: Submission[]): Submission[] {
  const filters = useSubmissionsUIStore((s) => s.filters);
  const sortBy = useSubmissionsUIStore((s) => s.sortBy);

  return useMemo(() => {
    let result = [...submissions];

    // Text search (company_name, contact_email, contact_name)
    if (filters.query) {
      const q = filters.query.toLowerCase();
      result = result.filter(
        (s) =>
          s.company_name.toLowerCase().includes(q) ||
          s.contact_email.toLowerCase().includes(q) ||
          (s.contact_name?.toLowerCase().includes(q) ?? false),
      );
    }

    // Status filter (explicit selection → exact; none → active inbox). See
    // filterSubmissionsByStatus.
    result = filterSubmissionsByStatus(result, filters.statuses);

    // Score range
    if (filters.minScore !== null) {
      result = result.filter((s) => (s.ai_score ?? 0) >= filters.minScore!);
    }
    if (filters.maxScore !== null) {
      result = result.filter((s) => (s.ai_score ?? 100) <= filters.maxScore!);
    }

    // Date range
    if (filters.dateRange !== 'all') {
      const days = { '7d': 7, '30d': 30, '90d': 90 }[filters.dateRange];
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);
      result = result.filter((s) => new Date(s.created_at) >= cutoff);
    }

    // Sort
    switch (sortBy) {
      case 'newest':
        result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
      case 'oldest':
        result.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        break;
      case 'score-high':
        result.sort((a, b) => (b.ai_score ?? 0) - (a.ai_score ?? 0));
        break;
      case 'score-low':
        result.sort((a, b) => (a.ai_score ?? 0) - (b.ai_score ?? 0));
        break;
      case 'company-az':
        result.sort((a, b) => a.company_name.localeCompare(b.company_name));
        break;
      case 'company-za':
        result.sort((a, b) => b.company_name.localeCompare(a.company_name));
        break;
    }

    return result;
  }, [submissions, filters, sortBy]);
}
